import { BasketIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetClient } from '@/api/market-shop/client/get-client'
import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { useGetMarketShopCategories } from '@/api/market-shop/products/get-market-shop-categories'
import { useGetMarketShopProducts } from '@/api/market-shop/products/get-market-shop-products'
import { Alert } from '@/components/ui/alert'
import { Icon } from '@/components/ui/icon'
import { popupFactory } from '@/factories'
import { useAutoPageReload, useFilters, useInactivityCheck } from '@/hooks'
import { getMarketShopStatus } from '@/pages/market-shop/market-shop.utils'
import { getApiErrorMessage } from '@/utils'

import { CheckOut } from './check-out'
import { EmptyState } from './empty-state'
import { Header } from './header'
import { InactivityModal } from './inactivity-modal'
import {
  CHECK_FOR_OPEN_STATUS_INTERVAL,
  MARKET_SHOP_AUTO_RELOAD_KEY,
  marketShopProductsInitialFilters,
} from './market-shop.constants'
import { useMarketShopStore } from './market-shop.store'
import * as S from './market-shop.styled'
import { ProductsContainer } from './products-container'
import { ShoppingCart } from './shopping-cart'

export const MarketShopPage = () => {
  const { t } = useTranslation(['market-shop'])
  const marketShopStore = useMarketShopStore()
  const hasProductsInCart = marketShopStore.hasProductsInCart()
  const totalProductsCountInCart = marketShopStore.getTotalProductsCountInCart()

  const {
    error: settingsError,
    isError: isSettingsError,
    isPending: isSettingsPending,
    isResidentFlow,
    data: settings,
  } = useGetSettingsByToken()

  const { data: client, isFetching: isClientFetching } = useGetClient({
    enabled: isResidentFlow,
  })

  const filters = useFilters({
    initialState: marketShopProductsInitialFilters,
  })

  const hasActiveFilters = !!filters.filters.search || !!filters.filters.categoryId?.length

  const {
    data: categories,
    isPending: isCategoriesPending,
    error: categoriesError,
    isError: isCategoriesError,
  } = useGetMarketShopCategories()

  const handleOpenShoppingCart = () => {
    popupFactory.open(ShoppingCart, {})
  }

  useInactivityCheck(
    (resumeInactivityTracking) => {
      popupFactory.open(
        InactivityModal,
        {
          onActive: resumeInactivityTracking,
          onInactive: () => {
            marketShopStore.reset()
            resumeInactivityTracking(false)
          },
        },
        { suspendCurrent: true },
      )
    },
    { enabled: hasProductsInCart && !popupFactory.checkIfOpen(CheckOut) && !isResidentFlow },
  )

  useAutoPageReload({
    storageKey: MARKET_SHOP_AUTO_RELOAD_KEY,
  })

  const useGetMarketShopProductsResult = useGetMarketShopProducts({
    categoryId: filters.filters.categoryId,
    limit: filters.filters.limit,
    ...(filters.filters.search?.trim() && {
      search: filters.filters.search.trim(),
    }),
  })

  const {
    data: products,
    isPending: isProductsPending,
    isFetching: isProductsFetching,
    error: productsError,
    isError: isProductsError,
    isFetchingNextPage,
  } = useGetMarketShopProductsResult
  const totalCount = products?.pages[0].count ?? 0

  const isError = isProductsError || isCategoriesError || isSettingsError
  const errorMessage = getApiErrorMessage(productsError, categoriesError, settingsError)

  useEffect(() => {
    // NOTE: Props drilling "free" side effect to clean up filters
    filters.handleResetFilters()
  }, [marketShopStore.resetCount])

  useEffect(() => {
    if (!isResidentFlow || !settings || !client) return

    const checkForOpenState = () => {
      const marketShopStatus = getMarketShopStatus(settings, client.buildingId.timezone, t)
      marketShopStore.setIsOpened(marketShopStatus.isOpen)
    }

    checkForOpenState()

    const interval = setInterval(() => {
      checkForOpenState()
    }, CHECK_FOR_OPEN_STATUS_INTERVAL)

    return () => clearInterval(interval)
  }, [isResidentFlow, settings, client, t])

  // NOTE: Handling a case of initial loading, so the loader takes up to whole page first time
  if (
    isCategoriesPending ||
    (!isFetchingNextPage && !hasActiveFilters && isProductsPending) ||
    isSettingsPending ||
    isClientFetching
  ) {
    return (
      <S.Root>
        <S.RootLoader size="large" />
      </S.Root>
    )
  }

  if (isError) {
    return (
      <S.Root $empty={true}>
        <S.ErrorContainer>
          <Alert type="error" title={errorMessage} />
        </S.ErrorContainer>
      </S.Root>
    )
  }

  // NOTE: special case -> if there are no catalog products at all
  if (!hasActiveFilters && !totalCount && !isProductsFetching) {
    return (
      <S.Root $empty={true}>
        <EmptyState
          title={t('market-shop:page.emptyTitle')}
          message={t('market-shop:page.emptyDescription')}
        />
      </S.Root>
    )
  }

  return (
    // NOTE: changing Root's key by sessionId will reset all downstream components to net-fresh ones
    // hence, complete the new session from scratch
    <S.Root key={marketShopStore.sessionId}>
      <Header categories={categories} useFiltersOutput={filters} />
      <ProductsContainer useGetMarketShopProductsResult={useGetMarketShopProductsResult} />
      {hasProductsInCart && (
        <S.ShoppingCartButton size="large" onClick={handleOpenShoppingCart}>
          <Icon icon={BasketIcon} />
          {t('market-shop:page.shoppingCartButton', { count: totalProductsCountInCart })}
        </S.ShoppingCartButton>
      )}
    </S.Root>
  )
}
